import { useReducer, useEffect, createContext, useContext, useRef, useMemo, useCallback } from 'react';
import { Platform } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useCountry } from './useCountry';
import { useTranslation } from './useTranslation';
import { campaignCache, CampaignData } from '@/services/campaignCacheService';
import { persistentCampaignCache } from '@/services/persistentCampaignCacheService';
import { unifiedStartupCache } from '@/services/unifiedStartupCache';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';

const logger = createScopedLogger('CampaignConfig');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

/**
 * Campaign Configuration Hook
 *
 * LANGUAGE SELECTION PRIORITY:
 * - For MA country (default/fallback): French (fr) is prioritized first
 * - For other countries: User language -> Device language -> French -> First available
 *
 * CONTENT METADATA LANGUAGE PRIORITY:
 * - User's preferred interface language -> Device language -> Campaign config language -> French fallback
 *
 * CRITICAL ASSUMPTIONS:
 * - The country code from useCountry is ALWAYS valid (GeoIP ensures this via MA fallback)
 * - The database MUST have at least one entry for country='ma' and language='fr'
 * - If isAvailable=false, this indicates a database integrity issue, not user location issue
 */

type CampaignReadyCallback = (campaignId: string) => void;

interface CampaignConfigContextType {
  campaignCountriesLanguagesId: string | null;
  fallbackCampaignCountriesLanguagesId: string | null;
  languagePreferredCampaignId: string | null;
  contentMetadataCampaignId: string | null;
  freeEpisodesCount: number;
  isLoading: boolean;
  isAvailable: boolean;
  isReady: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  availableLanguages: string[];
  registerReadyCallback: (callback: CampaignReadyCallback) => void;
  revenueCatEnabledApp: boolean;
  revenueCatEnabledWeb: boolean;
  dcbEnabledWeb: boolean;
}

const CampaignConfigContext = createContext<CampaignConfigContextType>({
  campaignCountriesLanguagesId: null,
  fallbackCampaignCountriesLanguagesId: null,
  languagePreferredCampaignId: null,
  contentMetadataCampaignId: null,
  freeEpisodesCount: 3,
  isLoading: true,
  isAvailable: false,
  isReady: false,
  error: null,
  refetch: async () => {},
  availableLanguages: [],
  registerReadyCallback: () => {},
  revenueCatEnabledApp: false,
  revenueCatEnabledWeb: false,
  dcbEnabledWeb: false,
});

export const useCampaignConfig = () => {
  const context = useContext(CampaignConfigContext);
  if (!context) {
    throw new Error('useCampaignConfig must be used within a CampaignConfigProvider');
  }
  return context;
};

interface CampaignState {
  campaignCountriesLanguagesId: string | null;
  fallbackCampaignCountriesLanguagesId: string | null;
  languagePreferredCampaignId: string | null;
  contentMetadataCampaignId: string | null;
  freeEpisodesCount: number;
  isLoading: boolean;
  isAvailable: boolean;
  isReady: boolean;
  error: string | null;
  availableLanguages: string[];
  revenueCatEnabledApp: boolean;
  revenueCatEnabledWeb: boolean;
  dcbEnabledWeb: boolean;
}

type CampaignAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: Omit<CampaignState, 'isLoading' | 'isReady' | 'error'> }
  | { type: 'FETCH_EMPTY'; payload: { fallbackCampaignCountriesLanguagesId: string | null } }
  | { type: 'FETCH_ERROR'; payload: { error: string } }
  | { type: 'NO_SUPABASE' };

const initialState: CampaignState = {
  campaignCountriesLanguagesId: null,
  fallbackCampaignCountriesLanguagesId: null,
  languagePreferredCampaignId: null,
  contentMetadataCampaignId: null,
  freeEpisodesCount: 3,
  isLoading: true,
  isAvailable: false,
  isReady: false,
  error: null,
  availableLanguages: [],
  revenueCatEnabledApp: false,
  revenueCatEnabledWeb: false,
  dcbEnabledWeb: false,
};

function campaignReducer(state: CampaignState, action: CampaignAction): CampaignState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...action.payload,
        isLoading: false,
        isReady: true,
        error: null,
      };
    case 'FETCH_EMPTY':
      return {
        ...initialState,
        fallbackCampaignCountriesLanguagesId: action.payload.fallbackCampaignCountriesLanguagesId,
        isLoading: false,
        isAvailable: false,
        isReady: false,
      };
    case 'FETCH_ERROR':
      return {
        ...state,
        error: action.payload.error,
        isAvailable: false,
        isLoading: false,
      };
    case 'NO_SUPABASE':
      return { ...state, isAvailable: false, isLoading: false };
    default:
      return state;
  }
}

export const CampaignConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const isMountedRef = useRef(true);
  const [state, dispatch] = useReducer(campaignReducer, initialState);
  const deviceLanguageRef = useRef<string>('en');
  const readyCallbacksRef = useRef<Array<CampaignReadyCallback>>([]);
  const hasTriggeredReadyRef = useRef(false);
  const readyCampaignIdRef = useRef<string | null>(null);

  const { countryCode, isLoading: countryLoading } = useCountry();
  const { language, isLoading: translationLoading, setCampaignRefetchCallback } = useTranslation();

  useEffect(() => {
    isMountedRef.current = true;

    const detectDeviceLanguage = () => {
      try {
        if (Platform.OS === 'web') {
          const browserLang = navigator.language || navigator.languages?.[0] || 'en';
          if (browserLang.startsWith('fr')) return 'fr';
          if (browserLang.startsWith('es')) return 'es';
          if (browserLang.startsWith('it')) return 'it';
          if (browserLang.startsWith('am')) return 'am';
          return 'en';
        } else {
          const Localization = require('expo-localization');
          const deviceLocale = Localization.getLocales()[0]?.languageCode || 'en';
          if (deviceLocale.startsWith('fr')) return 'fr';
          if (deviceLocale.startsWith('es')) return 'es';
          if (deviceLocale.startsWith('it')) return 'it';
          if (deviceLocale.startsWith('am')) return 'am';
          return 'en';
        }
      } catch (error) {
        console.error('Error detecting device language:', error);
        return 'en';
      }
    };

    deviceLanguageRef.current = detectDeviceLanguage();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCampaignRefetchCallback(refetch);
  }, []);

  useEffect(() => {
    if (!countryLoading && !translationLoading && countryCode && language) {
      fetchCampaignConfig();
    }
  }, [countryCode, language, countryLoading, translationLoading]);

  const registerReadyCallback = useCallback((callback: CampaignReadyCallback) => {
    if (hasTriggeredReadyRef.current && readyCampaignIdRef.current) {
      logWithTimestamp(`🎯 CampaignConfig: Callback registered after ready - executing immediately with campaignId: ${readyCampaignIdRef.current}`);
      callback(readyCampaignIdRef.current);
    } else {
      readyCallbacksRef.current.push(callback);
    }
  }, []);

  const triggerReadyCallbacks = (campaignId: string) => {
    if (hasTriggeredReadyRef.current) {
      return;
    }

    hasTriggeredReadyRef.current = true;
    readyCampaignIdRef.current = campaignId;
    const timestamp = new Date().toISOString();

    logWithTimestamp(`[${timestamp}] 🎯 CampaignConfig: ✅ READY - Firing ${readyCallbacksRef.current.length} registered callbacks with campaignId: ${campaignId}`);

    readyCallbacksRef.current.forEach((callback, index) => {
      try {
        logWithTimestamp(`[${timestamp}] 🎯 CampaignConfig: Executing callback ${index + 1}/${readyCallbacksRef.current.length}`);
        callback(campaignId);
      } catch (error) {
        errorWithTimestamp(`[${timestamp}] 🎯 CampaignConfig: Error in callback ${index + 1}:`, error);
      }
    });

    readyCallbacksRef.current = [];
  };

  const processAndSetCampaignConfigs = (
    allConfigs: any[],
    frenchFallbackId: string | null,
    languages: string[],
    countryCodeLower: string,
    userLanguage: string
  ) => {
    if (!allConfigs || allConfigs.length === 0) {
      if (isMountedRef.current) {
        dispatch({ type: 'FETCH_EMPTY', payload: { fallbackCampaignCountriesLanguagesId: frenchFallbackId } });
      }
      return;
    }

    let selectedConfig;
    let languagePreferredConfig;
    const isMAFallback = countryCodeLower === 'ma';

    if (isMAFallback) {
      // Primary/billing config keeps the MA default language (Arabic). This drives
      // pricing, free-episode count and DCB/RevenueCat flags — not content language.
      selectedConfig = allConfigs.find(c => c.language_code === 'ar') ||
                       allConfigs.find(c => c.language_code === userLanguage) ||
                       allConfigs[0];
      // Content metadata (series titles, descriptions) must follow the user's
      // SELECTED language, not the MA default — otherwise MA users always see
      // titles in the default language regardless of the language they choose.
      // Falls back to the primary config only when the selected language has no
      // config for this country.
      languagePreferredConfig = allConfigs.find(c => c.language_code === userLanguage) || selectedConfig;
    } else {
      selectedConfig = allConfigs.find(c => c.language_code === userLanguage);
      languagePreferredConfig = selectedConfig;

      if (!selectedConfig) {
        selectedConfig = allConfigs.find(c => c.language_code === deviceLanguageRef.current) ||
                         allConfigs.find(c => c.language_code === 'en') ||
                         allConfigs[0];
      }
    }

    if (selectedConfig && isMountedRef.current) {
      const contentMetadataConfig = languagePreferredConfig ||
                                     allConfigs.find(c => c.language_code === deviceLanguageRef.current) ||
                                     selectedConfig;

      dispatch({
        type: 'FETCH_SUCCESS',
        payload: {
          campaignCountriesLanguagesId: selectedConfig.id,
          fallbackCampaignCountriesLanguagesId: frenchFallbackId,
          languagePreferredCampaignId: languagePreferredConfig?.id || null,
          contentMetadataCampaignId: contentMetadataConfig.id,
          freeEpisodesCount: selectedConfig.free_episodes_count ?? 3,
          isAvailable: true,
          availableLanguages: languages,
          revenueCatEnabledApp: selectedConfig.revenue_cat_enabled_app === true,
          revenueCatEnabledWeb: selectedConfig.revenue_cat_enabled_web === true,
          dcbEnabledWeb: selectedConfig.dcb_enabled_web === true,
        },
      });

      logWithTimestamp(`🎯 CampaignConfig: ✅ Config set (${selectedConfig.language_code}, freeEpisodesCount: ${selectedConfig.free_episodes_count ?? 3})`);

      triggerReadyCallbacks(selectedConfig.id);
    }
  };

  const fetchCampaignConfig = async () => {
    if (!countryCode || !language) {
      return;
    }

    try {
      dispatch({ type: 'FETCH_START' });

      if (!supabase) {
        if (isMountedRef.current) {
          dispatch({ type: 'NO_SUPABASE' });
        }
        return;
      }

      const countryCodeLower = countryCode.toLowerCase();

      const startupCache = await unifiedStartupCache.get();
      if (startupCache &&
          startupCache.allCampaignConfigs.length > 0 &&
          startupCache.countryCode.toLowerCase() === countryCodeLower &&
          !unifiedStartupCache.needsRefresh('campaign')) {

        logWithTimestamp(`🎯 CampaignConfig: ✅ Using unified cache (${startupCache.allCampaignConfigs.length} configs)`);

        const allConfigs = startupCache.allCampaignConfigs;
        const frenchFallback = startupCache.frenchFallbackId;

        campaignCache.clear();
        allConfigs.forEach(config => {
          campaignCache.set({
            id: config.id,
            campaign_id: config.campaign_id,
            country_code: config.country_code,
            language_code: config.language_code,
            culture: config.culture,
            free_episode_price_coins: config.free_episode_price_coins,
            fallback_language_code: config.fallback_language_code,
            free_episodes_count: config.free_episodes_count ?? 3,
            revenue_cat_enabled_app: config.revenue_cat_enabled_app === true,
            revenue_cat_enabled_web: config.revenue_cat_enabled_web === true,
            dcb_enabled_web: config.dcb_enabled_web === true,
          });
        });
        campaignCache.markInitialized();
        logWithTimestamp(`🎯 CampaignConfig: ✅ In-memory cache populated with ${allConfigs.length} configs`);

        processAndSetCampaignConfigs(
          allConfigs,
          frenchFallback,
          startupCache.availableLanguages,
          countryCodeLower,
          language
        );

        return;
      }

      logWithTimestamp('🎯 CampaignConfig: Fetching fresh configs (cache miss or stale)...');

      const [configsResult, frenchFallbackResult] = await Promise.all([
        withSupabaseResult<any[]>(
          supabase
            .from('campaign_countries_languages')
            .select('id, language_code, campaign_id, country_code, culture, free_episode_price_coins, fallback_language_code, free_episodes_count, revenue_cat_enabled_app, revenue_cat_enabled_web, dcb_enabled_web')
            .eq('country_code', countryCodeLower),
          { operation: 'campaign.fetchConfigs', logger, fallback: [] }
        ),
        withSupabaseResult<{ id: string } | null>(
          supabase
            .from('campaign_countries_languages')
            .select('id')
            .eq('country_code', 'fr')
            .eq('language_code', 'fr')
            .maybeSingle(),
          { operation: 'campaign.fetchFrenchFallback', logger }
        )
      ]);

      const { data: allConfigs, error: fetchAllError } = configsResult;
      const { data: frenchFallback, error: frenchError } = frenchFallbackResult;

      if (fetchAllError) {
        errorWithTimestamp('🎯 CampaignConfig: Database fetch error:', fetchAllError);
        if (isMountedRef.current) {
          dispatch({ type: 'FETCH_ERROR', payload: { error: 'Failed to load campaign configuration' } });
        }
        return;
      }

      if (frenchError) {
        errorWithTimestamp('🎯 CampaignConfig: French fallback fetch error:', frenchError);
      }

      if (!allConfigs || allConfigs.length === 0) {
        errorWithTimestamp('🎯 CampaignConfig: No configs found for country:', countryCodeLower);
        processAndSetCampaignConfigs([], frenchFallback?.id || null, [], countryCodeLower, language);
        return;
      }

      campaignCache.clear();
      const campaignDataList: CampaignData[] = allConfigs.map((config) => ({
        id: config.id,
        campaign_id: config.campaign_id?.toString() || '',
        country_code: config.country_code || countryCodeLower,
        language_code: config.language_code,
        culture: config.culture || null,
        free_episode_price_coins: config.free_episode_price_coins || 0,
        fallback_language_code: config.fallback_language_code || null,
        free_episodes_count: config.free_episodes_count ?? 3,
        revenue_cat_enabled_app: config.revenue_cat_enabled_app === true,
        revenue_cat_enabled_web: config.revenue_cat_enabled_web === true,
        dcb_enabled_web: config.dcb_enabled_web === true,
      } as CampaignData));

      campaignDataList.forEach(data => campaignCache.set(data));
      campaignCache.markInitialized();
      logWithTimestamp(`🎯 CampaignConfig: ✅ In-memory cache populated with ${campaignDataList.length} configs BEFORE state update`);

      const languages = allConfigs.map(config => config.language_code);

      Promise.all([
        persistentCampaignCache.bulkSet(campaignDataList, countryCodeLower),
        unifiedStartupCache.updateCampaignData(campaignDataList, frenchFallback?.id || null, languages)
      ]).catch(err => errorWithTimestamp('Error updating caches:', err));

      processAndSetCampaignConfigs(
        allConfigs,
        frenchFallback?.id || null,
        languages,
        countryCodeLower,
        language
      );

    } catch (err) {
      errorWithTimestamp('🎯 CampaignConfig: Unexpected error:', err);
      if (isMountedRef.current) {
        dispatch({ type: 'FETCH_ERROR', payload: { error: err instanceof Error ? err.message : 'Unknown error' } });
      }
    }
  };

  const refetch = useCallback(async () => {
    await fetchCampaignConfig();
  }, [countryCode, language]);

  const value = useMemo<CampaignConfigContextType>(() => ({
    campaignCountriesLanguagesId: state.campaignCountriesLanguagesId,
    fallbackCampaignCountriesLanguagesId: state.fallbackCampaignCountriesLanguagesId,
    languagePreferredCampaignId: state.languagePreferredCampaignId,
    contentMetadataCampaignId: state.contentMetadataCampaignId,
    freeEpisodesCount: state.freeEpisodesCount,
    isLoading: state.isLoading,
    isAvailable: state.isAvailable,
    isReady: state.isReady,
    error: state.error,
    refetch,
    availableLanguages: state.availableLanguages,
    registerReadyCallback,
    revenueCatEnabledApp: state.revenueCatEnabledApp,
    revenueCatEnabledWeb: state.revenueCatEnabledWeb,
    dcbEnabledWeb: state.dcbEnabledWeb,
  }), [state, refetch, registerReadyCallback]);

  return (
    <CampaignConfigContext.Provider value={value}>
      {children}
    </CampaignConfigContext.Provider>
  );
};
